// Opportunity-related filters

import { CatalogType } from '@supply-mover-2-0/shared';
import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const opportunityFilters: FilterRegistry = {
	opportunityKeywords: {
		...createKeywordFilter({
			label: 'Keywords',
			placeholder: 'Search by name, client...',
			objectTable: 'opportunity',
			listKeys: ['opportunity'],
			fulltextFields: ['mSmName', 'client.name'],
		}),
		queryParamName: 'opportunityKeywords',
	},

	stage: {
		type: 'multi-select',
		label: 'Stage',
		placeholder: 'Select stages...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'mSmName' as keyof unknown,
			secondary: 'mSmProbability' as keyof unknown,
		},
		apiEndpoint: '/clients/opportunity-stages',
		queryParamName: 'stage',
		multiple: true,
		queryMapping: {
			objectTable: 'opportunity',
			fieldName: 'm_sm_fk_m_sm_client_opportunity_stage__id',
			joinType: 'direct',
		},
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['opportunity', 'opportunity-client-embedded'],
	},

	opportunityClient: {
		...createEntityLookupFilter({
			label: 'Client',
			placeholder: 'Search clients...',
			apiEndpoint: '/clients',
			objectTable: 'opportunity',
			fieldName: 'm_sm_fk_client__id',
			listKeys: ['opportunity'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'externalId',
			},
			colorScheme: 'green',
		}),
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'opportunityClient',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	opportunityUser: {
		...createEntityLookupFilter({
			label: 'Assigned To',
			placeholder: 'Search users...',
			apiEndpoint: '/users',
			objectTable: 'opportunity',
			fieldName: 'm_sm_fk_user__id',
			listKeys: ['opportunity', 'opportunity-client-embedded'],
			displayFields: {
				id: 'id',
				primary: 'display_name',
				secondary: 'm_sm_email',
			},
			colorScheme: 'purple',
		}),
		options: [{ id: 'ME', label: 'Me' }],
		queryParamName: 'opportunityUser',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	// PD-17794 — opportunities with no job, so they can be found and cleaned up.
	opportunityJob: {
		type: 'multi-select',
		label: 'Job',
		placeholder: 'Any job status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'opportunityJob',
		multiple: true,
		options: [
			{ id: 'null', label: 'No Job' },
			{ id: 'not_null', label: 'On a Job' },
		],
		queryMapping: {
			objectTable: 'opportunity',
			fieldName: 'fk_job__id',
			joinType: 'direct',
		},
		listKeys: ['opportunity'],
	},

	// PD-17794 — the tenant's Opportunity Types.
	opportunityType: {
		type: 'multi-select',
		label: 'Opportunity Type',
		placeholder: 'Select types...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/catalog-values',
		apiParams: { catalogType: CatalogType.OpportunityType },
		queryParamName: 'opportunityType',
		multiple: true,
		queryMapping: {
			objectTable: 'opportunity',
			fieldName: 'fk_catalog_value__id_opportunityType',
			joinType: 'direct',
		},
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['opportunity'],
	},

	opportunityAmount: {
		type: 'multi-select',
		label: 'Amount Range',
		placeholder: 'Select amount range...',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'opportunityAmount',
		multiple: true,
		options: [
			{ id: '0-10k', label: '$0 - $10K', data: { min: 0, max: 10000 } },
			{ id: '10k-50k', label: '$10K - $50K', data: { min: 10000, max: 50000 } },
			{
				id: '50k-100k',
				label: '$50K - $100K',
				data: { min: 50000, max: 100000 },
			},
			{
				id: '100k-500k',
				label: '$100K - $500K',
				data: { min: 100000, max: 500000 },
			},
			{
				id: '500k-1m',
				label: '$500K - $1M',
				data: { min: 500000, max: 1000000 },
			},
			{ id: '1m-plus', label: '$1M+', data: { min: 1000000, max: null } },
		],
		queryMapping: {
			objectTable: 'opportunity',
			fieldName: 'm_sm_amount',
			joinType: 'custom',
		},
		listKeys: ['opportunity', 'opportunity-client-embedded'],
	},

	opportunityDateRange: {
		...createDateRangeFilter({
			label: 'Close Date',
			objectTable: 'opportunity',
			dateField: 'm_sm_closeDate',
			listKeys: ['opportunity', 'opportunity-client-embedded'],
		}),
		queryParamName: 'opportunityDateRange',
	},
};
